import type { PlaylistEntry } from './runs-api.js';

// #93: DIE eine Stelle für die Gruppenregeln — Liste (playlist.tsx) UND Auto-Sprung
// (my-tests-page.tsx) lesen hieraus (#83-Lehre: eine Quelle, mehrere Leser).
// Abgrenzung zu #92: 'blocked' ohne retest-bereiten Defect bekommt weiterhin KEIN Aktions-Label
// (state bleibt 'default') — die Gruppe „Blockiert" kommt aus last_result und sagt „warte",
// nicht „teste nochmal". Retest-bereit ⇒ Gruppe Retest (state gewinnt vor last_result).
// Task 3 (A6): Die Identität eines Playlist-Eintrags ist das Paar (Testfall, Kette) — ein Testfall
// in zwei Ketten erscheint zweimal, mit eigenem Zustand. `PlaylistEntryRef` ist das Minimum, das
// einen Eintrag benennt; alles, was einen Eintrag ADRESSIERT (Auswahl-Zustand in my-tests-page,
// React-`key` und Auswahlvergleich in playlist.tsx, Remount-Schlüssel des ExecutionPanel, der
// Auto-Sprung unten), geht über `playlistEntryKey` — DIESE eine Stelle, nicht fünfmal `a|b`.
export interface PlaylistEntryRef {
  test_case_id: string;
  e2e_id: string | null;
}

// Trennzeichen '|': UUIDs (test_case_id/e2e_id) enthalten es nicht, der Schlüssel ist damit
// eindeutig zerlegbar — und der kettenlose Eintrag ('tc|') kann mit keinem Ketten-Eintrag
// kollidieren, weil eine e2e_id nie leer ist.
export function playlistEntryKey(ref: PlaylistEntryRef): string {
  return `${ref.test_case_id}|${ref.e2e_id ?? ''}`;
}

export type PlaylistGroupKey = 'retest' | 'open' | 'error' | 'blocked' | 'done';

export const GROUP_ORDER: readonly PlaylistGroupKey[] = [
  'retest',
  'open',
  'error',
  'blocked',
  'done',
];

// #195: `isHandoverEntry` (apps/api/src/services/handover/rules.ts) spiegelt diese Regel — mit ändern.
export function groupOf(entry: PlaylistEntry): PlaylistGroupKey {
  if (entry.state === 'retest') return 'retest';
  if (entry.last_result === 'successful') return 'done';
  if (entry.last_result === 'error') return 'error';
  if (entry.last_result === 'blocked') return 'blocked';
  // nie gelaufen bzw. 'open'. Task 4 (#113): Hier landet seitdem auch der neue Zustand `waiting`
  // (Vorgänger in der Kette noch nicht bestanden) — A4 verlangt ausdrücklich, dass gesperrte
  // Glieder SICHTBAR bleiben und nicht aus der Liste verschwinden. Die eigene Darstellung und der
  // getrennte Zähler („7 offen · 5 wartend") kommen in Task 10.
  return 'open';
}

// Task 10, Review-Befund Important 1: DIE EINE FRAGE „darf der Tester diesen Eintrag anfassen?".
//
// Sie haengt am VORGAENGER-URTEIL (`blocked_by`), NICHT am Anzeige-Zustand. `state` folgt der
// Rangfolge retest -> error -> waiting: ein Glied mit eigenem `error`-Lauf UND offenem Vorgaenger
// heisst 'error', nicht 'waiting'. Wer die Sperre an `state === 'waiting'` festmacht, laesst genau
// diese Eintraege durch — weiss, anklickbar, als „offen" gezaehlt, Ziel des Auto-Sprungs — und der
// Server weist sie mit 409 predecessor_not_done ab. Der Server selbst urteilt seit Task 5 ueber
// `predecessorGate`, also entkoppelt; seit dem Review von Task 10 traegt `entry.blocked_by`
// genau dieses Urteil (plan-status.ts).
//
// `state` bleibt zustaendig fuer FARBE UND LABEL — die Trennung ist der ganze Punkt: eine gesperrte
// Fehler-Karte sagt weiterhin „Fehler" und ist trotzdem nicht bedienbar.
//
// Whole-Branch-Review, Important 2: Seitdem traegt `blocked_by` das Urteil OHNE JEDE AUSNAHME —
// deckungsgleich mit dem Server-Gate (routes/runs.ts, 409 predecessor_not_done). Vorher nahm der
// Server eine Anzeige-Ruecksicht ins Feld („in DIESER Kette schon bestanden ⇒ null") und gab damit
// genau einen Lauf frei, den er selbst abweist: Glied 3 bestanden, Glied 2 spaeter auf `error`.
// Die Ruecksicht sitzt jetzt hier — in `isLocked`, wo sie hingehoert.
export function isBlocked(entry: PlaylistEntry): boolean {
  return entry.blocked_by !== null;
}

// Die ANZEIGE-Frage: „ist diese Karte zu sperren und als wartend zu zaehlen?"
//
// Unterschied zu `isBlocked` in genau einem Fall: ein ERLEDIGTES Glied. Es ist serverseitig
// tatsaechlich nicht wiederholbar, wenn sein Vorgaenger nachtraeglich wieder rot geworden ist —
// aber es ist erledigt, nicht wartend. Ausgegraut in der Gruppe „Erledigt" und im Wartend-Zaehler
// behauptete es Arbeit, wo keine ist. Wer stattdessen wissen will, ob ein LAUF durchgeht, fragt
// `isBlocked` (so das ExecutionPanel, das genau an dieser erledigten Karte den 409 abfaengt).
export function isLocked(entry: PlaylistEntry): boolean {
  return groupOf(entry) !== 'done' && isBlocked(entry);
}

// Task 10 (#113, A4): „freie Faelle oben, gesperrte unten; innerhalb der Gruppen wie bisher nach
// Kette und Sequenz". Die Server-Reihenfolge IST bereits Kette-und-Sequenz (buildPlaylist sortiert
// danach) — deshalb wird sie nicht neu gebildet, sondern nur in zwei Bloecke geteilt. `sort` ist in
// ES2019+ stabil, die Reihenfolge innerhalb beider Bloecke bleibt damit unangetastet.
//
// Angewandt JE GRUPPE, nicht auf die Rohliste: ein gesperrter Eintrag, dessen Vorgaenger
// nachtraeglich wieder rot geworden ist, faellt nach `groupOf` in die Gruppe 'blocked' — auch dort
// gehoert er unter die freien Faelle. Dasselbe gilt fuer die Gruppe 'error' (s. `isBlocked`).
function freeBeforeBlocked(entries: PlaylistEntry[]): PlaylistEntry[] {
  // `isLocked`, nicht `isBlocked` (Whole-Branch-Review, Important 2): in der Gruppe „Erledigt"
  // traegt seit der Entkopplung jedes Glied einer unterbrochenen Kette ein `blocked_by` — es nach
  // unten zu sortieren waere eine Aussage ueber erledigte Arbeit, die niemand gemacht hat.
  return [...entries].sort((a, b) => Number(isLocked(a)) - Number(isLocked(b)));
}

export function groupEntries(
  entries: PlaylistEntry[],
  isVisible: (entry: PlaylistEntry) => boolean,
): { key: PlaylistGroupKey; entries: PlaylistEntry[] }[] {
  return GROUP_ORDER.map((key) => ({
    key,
    entries: freeBeforeBlocked(entries.filter((e) => groupOf(e) === key && isVisible(e))),
  })).filter((g) => g.entries.length > 0);
}

// Task 10 (#113, A4): die GETRENNTEN Zaehler — „7 offen · 5 wartend".
//
// Bewusst NICHT aus den Gruppen abgeleitet (Review-Befund Task 4, Minor 4): `groupOf` sortiert
// einen gesperrten Eintrag nur dann nach 'open', wenn sein `last_result` null oder 'open' ist. Ist
// der Vorgaenger nachtraeglich wieder rot geworden und der Eintrag traegt selbst 'blocked' oder
// 'error', landet er in der Gruppe 'blocked' bzw. 'error'. Wer „wartend" aus den Gruppen zaehlt,
// zaehlt ihn nicht mit — und meldet ihn obendrein als offen.
//
// Und ebenso wenig aus `state` (Review-Befund Task 10, Important 1): die Rangfolge verdeckt die
// Sperre bei jedem Glied mit eigenem error-/retest-Lauf. Gezaehlt wird deshalb ueber `isBlocked` —
// dieselbe Frage, die auch ueber Anklickbarkeit und Sprungziel entscheidet.
//
// „offen" = alles, was der Tester jetzt anfassen kann: nicht erledigt und nicht gesperrt. Fehler-
// und Retest-Eintraege sind Arbeit und zaehlen mit. Gezaehlt wird ueber die SICHTBAREN Eintraege,
// damit die Summe zu den Zaehlern der Gruppenueberschriften passt (die filtern ebenso).
export function playlistCounts(
  entries: PlaylistEntry[],
  isVisible: (entry: PlaylistEntry) => boolean,
): { open: number; waiting: number } {
  let open = 0;
  let waiting = 0;
  for (const e of entries) {
    if (!isVisible(e)) continue;
    // Erledigt zuerst — beides ist „keine offene Arbeit" (Whole-Branch-Review, Important 2). Seit
    // `blocked_by` das Urteil ohne Ausnahme traegt, wuerde ein bestandenes Glied mit rotem
    // Vorgaenger sonst im Wartend-Zaehler landen.
    if (groupOf(e) === 'done') continue;
    if (isBlocked(e)) waiting += 1;
    else open += 1;
  }
  return { open, waiting };
}

// EXE-1 nach #93: nächster nicht-erledigter Eintrag NACH dem aktuellen (Server-Reihenfolge =
// E2E-Priorisierung), am Listenende zum Anfang wickelnd. Erledigte werden still übersprungen
// (das tat die alte Playlist über ihren Server-Filter implizit auch); WEGGEFILTERTE werden
// übersprungen UND gemeldet — der Tester hat den Filter bewusst gesetzt, soll ihn aber beim
// Sprung nicht vergessen (Klärungsprotokoll: EIN Sammel-Toast je Sprung).
export function nextAfterSubmit(
  entries: PlaylistEntry[],
  // Task 3: der SCHLÜSSEL des gerade abgeschickten Eintrags (playlistEntryKey), nicht seine
  // test_case_id — sonst fände `findIndex` bei einem Testfall in zwei Ketten immer das erste
  // Vorkommen und der Sprung liefe ab der falschen Listenstelle weiter.
  currentKey: string | null,
  isVisible: (entry: PlaylistEntry) => boolean,
): { next: PlaylistEntry | null; skippedFiltered: boolean } {
  const start = entries.findIndex((e) => playlistEntryKey(e) === currentKey);
  // Math.max(start, 0): fehlt/veraltet currentKey (-1) ⇒ ab Listenanfang scannen. Ohne den Schutz
  // würde `slice(0, -1)` bei unbekanntem currentKey still den letzten Listeneintrag verwerfen.
  const rotated = [...entries.slice(start + 1), ...entries.slice(0, Math.max(start, 0))];
  let skippedFiltered = false;
  for (const candidate of rotated) {
    if (groupOf(candidate) === 'done') continue;
    // Task 10 (#113, A4): Ein gesperrtes Glied ist KEIN gueltiges Sprungziel — der Server weist den
    // Lauf mit 409 predecessor_not_done ab (Task 5). Bis hierher sprang der Tester nach jedem Lauf
    // womoeglich auf eine ausgegraute Karte und lief in einen Fehler, den er nicht verursacht hat.
    // Gefragt wird `isBlocked` und nicht `state` (Review-Befund Important 1): sonst bleibt jedes
    // Glied mit eigenem error-/retest-Lauf und offenem Vorgaenger ein gueltiges Sprungziel.
    // Ohne `skippedFiltered`: uebersprungen wird hier wegen der SPERRE, nicht wegen des Filters —
    // der Sammel-Toast „Filter aktiv" waere an dieser Stelle eine falsche Erklaerung.
    if (isBlocked(candidate)) continue;
    if (!isVisible(candidate)) {
      skippedFiltered = true;
      continue;
    }
    return { next: candidate, skippedFiltered };
  }
  return { next: null, skippedFiltered };
}
